import React, {ReactNode} from 'react'
import {OptionSetting} from './FacilityOptionInputForm'
import { Box, Card, CardHeader, Avatar, IconButton } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import BuildIcon from '@mui/icons-material/Build'
import {LeftColumnFlexContainer} from '../../../../styledComponents/FlexComponents'
import styled from 'styled-components'
import {formatNumberWithCommas} from '../../../../formatters/formatNumberWithCommas'
import {UNIT_TIME, VALID_OR_NOT} from '../../../../CodeMaster'
import equal from 'fast-deep-equal'
import EventEmitterExAdmin from '../../../shared/pageBaseComponents/EventEmitterExAdmin'
import PPErrorLabel from '../../../shared/forms/PPErrorLabel'

interface FacilityOptionListProps {
    options: OptionSetting[]
    eventEmitter: EventEmitterExAdmin
    onRequestDeleteOption: (id: string) => void
    onRequestUpdateOption: (id: string) => void
}

class FacilityOptionList extends React.Component<FacilityOptionListProps> {
    constructor(props: Readonly<FacilityOptionListProps>) {
        super(props)

        this.handleRequestDelete = this.handleRequestDelete.bind(this)
        this.handleRequestUpdate = this.handleRequestUpdate.bind(this)
    }

    shouldComponentUpdate(nextProps: Readonly<FacilityOptionListProps>): boolean {
        return !(this.props === nextProps || equal(this.props, nextProps))
    }

    handleRequestDelete = (id: string) => (event: React.MouseEvent): void => {
        this.props.onRequestDeleteOption(id)
    }
    handleRequestUpdate = (id: string) => (event: React.MouseEvent): void => {
        this.props.onRequestUpdateOption(id)
    }

    createChargeString(option: OptionSetting): string {
        if (option.charge === 0) {
            return '無料'
        }
        const chargeWithUnit: string = `${formatNumberWithCommas(option.charge)}円`
        if (option.isFixedCharge === VALID_OR_NOT.VALID) {
            return `${chargeWithUnit}/固定`
        }

        let unitTimeValue: string = ''
        if (option.unitTimeSuffix === UNIT_TIME.HOUR) {
            unitTimeValue = `${option.unitTimeHourValue}`
        } else {
            unitTimeValue = (option.unitTimeMinutesValue) ? option.unitTimeMinutesValue.label : ''
        }
        const unitTimeSuffix: string = (option.unitTimeSuffix) ? option.unitTimeSuffix.label : ''
        return `${chargeWithUnit}/${unitTimeValue}${unitTimeSuffix}`
    }

    createRequiredString(required: VALID_OR_NOT): string {
        return (required === VALID_OR_NOT.VALID) ? '必須' : '任意'
    }

    renderOptionCard(option: OptionSetting): ReactNode {
        const localId: string = option.localId
        const requiredString: string = this.createRequiredString(option.required)
        const chargeString: string = this.createChargeString(option)
        const quantityRange: string =
            (option.minQuantity !== option.maxQuantity)
                ? `${option.minQuantity}〜${option.maxQuantity}${option.quantitySuffix}`
                : `${option.minQuantity}${option.quantitySuffix}`
        const paymentType: string = option.charge === 0 ? '' : option.paymentType.label

        return (
            <FacilityOptionCard key={localId}>
                <Card square>
                    <CardHeader
                        style={{padding: '8px'}}
                        avatar={
                            <Avatar>{option.sortOrder}</Avatar>
                        }
                        action={<Box padding={'8px 8px 0 0'}>
                            <LeftColumnFlexContainer>
                                <IconButton onClick={this.handleRequestUpdate(localId)} size="large">
                                    <BuildIcon titleAccess={'編集'}/>
                                </IconButton>
                                <IconButton onClick={this.handleRequestDelete(localId)} size="large">
                                    <DeleteForeverIcon titleAccess={'削除'}/>
                                </IconButton>
                            </LeftColumnFlexContainer>
                        </Box>}
                        title={option.name}
                        subheader={
                            <LeftColumnFlexContainer>
                                <div>{quantityRange}（{requiredString}）</div>
                                <div>{chargeString}</div>
                                <div>{paymentType}</div>
                            </LeftColumnFlexContainer>
                        }
                    />
                    <PPErrorLabel
                        eventEmitter={this.props.eventEmitter}
                        eventKeys={[
                            `optionSettings[${option.localId}].name`,
                            `optionSettings[${option.localId}].sortOrder`,
                            `optionSettings[${option.localId}].charge`,
                            `optionSettings[${option.localId}].chargeUnit`,
                            `optionSettings[${option.localId}].volumeMin`,
                            `optionSettings[${option.localId}].volumeMax`,
                            `optionSettings[${option.localId}].paymentMethod`,
                            `optionSettings[${option.localId}].appointRequired`,
                            `optionSettings[${option.localId}].unitName`,
                        ]}
                    />
                </Card>
            </FacilityOptionCard>
        );
    }

    render(): ReactNode {
        return (
            <>
                {
                    [...this.props.options]
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((option) => this.renderOptionCard(option))
                }
            </>
        )
    }
}

export default FacilityOptionList

const FacilityOptionCard = styled.div`
  display: inline-block;
  margin-top: 8px;
  margin-right: 8px;
`
